import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CompanyConfigService } from '@shabic/core';
import { ICompanyConfigRequest, CompanyReportingMethod, Company } from '@shabic/models';
import { CompanyService } from '@shabic/core';
import { ChangeDetectorRef } from '@angular/core';
import { SnackBarService } from '@shabic/core';

@Component({
  selector: 'shabic-report-setting',
  templateUrl: './report-setting.component.html',
  styleUrls: ['./report-setting.component.scss']
})
export class ReportSettingComponent {
  form: FormGroup;
  loading = false;
  success = false;
  error: string | null = null;
  companyId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private companyConfigService: CompanyConfigService,
    private companyService: CompanyService,
    private cdr: ChangeDetectorRef,
    private snackBarService: SnackBarService
  ) {
    this.form = this.fb.group({
      method: ['mail', Validators.required],
      email: ['', []],
      apiUrl: ['', []],
      apiKey: ['', []],
      apiUsername: ['', []],
      apiPassword: ['', []],
      folderUrl: ['', []],
    });
    this.loadCompanyId();
    this.form.get('method')?.valueChanges.subscribe(method => {
      this.updateValidators(method);
    });
    this.updateValidators(this.form.get('method')?.value);
  }

  private loadCompanyId() {
    this.companyService.getMyCompany().subscribe(response => {
      if (response.payload instanceof Company) {
        this.companyId = response.payload.id;
        this.loadCompanyConfig();
      }
    });
  }

  private loadCompanyConfig() {
    if (!this.companyId) return;
    this.companyConfigService.getConfig(this.companyId).subscribe(config => {
      if (config.reportingMethod === 'MAIL') {
        this.form.patchValue({
          method: 'mail',
          email: config.mailUrl || ''
        });
      } else if (config.reportingMethod === 'API') {
        this.form.patchValue({
          method: 'api',
          apiUrl: config.apiUrl || '',
          apiKey: config.apiKey || '',
          apiUsername: config.apiUsername || '',
          apiPassword: config.apiPassword || ''
        });
      } else if (config.reportingMethod === 'SHARED_FOLDER') {
        this.form.patchValue({
          method: 'shared-folder',
          folderUrl: config.sharedFolderUrl || ''
        });
      }
      this.cdr.detectChanges();
    });
  }

  private updateValidators(method: string) {
    // Reset all validators
    this.form.get('email')?.clearValidators();
    this.form.get('apiUrl')?.clearValidators();
    this.form.get('apiKey')?.clearValidators();
    this.form.get('apiUsername')?.clearValidators();
    this.form.get('apiPassword')?.clearValidators();
    this.form.get('folderUrl')?.clearValidators();

    if (method === 'mail') {
      this.form.get('email')?.setValidators([Validators.required, Validators.email]);
    } else if (method === 'api') {
      this.form.get('apiUrl')?.setValidators([Validators.required]);
      this.form.get('apiKey')?.setValidators([Validators.required]);
      this.form.get('apiUsername')?.setValidators([Validators.required]);
      this.form.get('apiPassword')?.setValidators([Validators.required]);
    } else if (method === 'shared-folder') {
      this.form.get('folderUrl')?.setValidators([Validators.required]);
    }
    this.form.get('email')?.updateValueAndValidity();
    this.form.get('apiUrl')?.updateValueAndValidity();
    this.form.get('apiKey')?.updateValueAndValidity();
    this.form.get('apiUsername')?.updateValueAndValidity();
    this.form.get('apiPassword')?.updateValueAndValidity();
    this.form.get('folderUrl')?.updateValueAndValidity();
  }

  onSave() {
    if (this.form.invalid || !this.companyId) return;
    this.loading = true;
    this.success = false;
    this.error = null;

    const method = this.form.value.method;
    let reportingMethod: CompanyReportingMethod = 'MAIL';
    if (method === 'api') reportingMethod = 'API';
    else if (method === 'shared-folder') reportingMethod = 'SHARED_FOLDER';
    else reportingMethod = 'MAIL';

    const body: ICompanyConfigRequest = {
      companyId: this.companyId,
      reportingMethod,
      mailUrl: method === 'mail' ? this.form.value.email : undefined,
      sharedFolderUrl: method === 'shared-folder' ? this.form.value.folderUrl : undefined,
      apiUrl: method === 'api' ? this.form.value.apiUrl : undefined,
      apiKey: method === 'api' ? this.form.value.apiKey : undefined,
      apiUsername: method === 'api' ? this.form.value.apiUsername : undefined,
      apiPassword: method === 'api' ? this.form.value.apiPassword : undefined,
    };

    this.companyConfigService.saveConfig(body).subscribe({
      next: () => {
        this.loading = false;
        this.success = true;
        this.snackBarService.openSnackBar('message.config-saved-successfully');
      },
      error: err => {
        this.loading = false;
        this.error = 'Failed to save settings.';
      }
    });
  }
} 